'use client';

import Script from 'next/script';
import { useEffect } from 'react';
import { usePathname } from 'next/navigation';

import useTenant from '@/state/tenant';
import {
  captureFbclidAsFbc,
  isValidGA4Id,
  isValidGtmId,
  isValidMetaPixelId,
  isValidOpenAiPixelId,
  trackGAPageview,
  trackMetaConversion,
} from '@/lib/analytics';
import { NODE_ENV } from '@/lib/env';
import { isGiftPagePath } from '@/lib/gift-page';

export function AnalyticsProvider() {
  const { data: tenant } = useTenant();
  const pathname = usePathname();

  // No tag loads on the gift recipient page and no pageview leaves it: every
  // destination would be handed its path, which is a gift card code.
  const trackingConfig = isGiftPagePath(pathname)
    ? null
    : tenant?.tracking_config;
  const gaId = trackingConfig?.google_analytics_id;
  const gtmId = trackingConfig?.google_tag_manager_id;
  const pixelId = trackingConfig?.meta_pixel_id;
  const oaiPixelId = trackingConfig?.openai_pixel_id;

  const hasGtm = isValidGtmId(gtmId);
  // A container usually carries its own GA4 tag, so direct GA4 stands down
  // while one is set — otherwise every pageview and purchase double-counts.
  // The admin disables the GA4 field too, but legacy rows can hold both ids.
  const hasGA = isValidGA4Id(gaId) && !hasGtm;
  const hasPixel = isValidMetaPixelId(pixelId);
  const hasOaiPixel = isValidOpenAiPixelId(oaiPixelId);

  useEffect(() => {
    if (hasPixel) captureFbclidAsFbc();
  }, [hasPixel]);

  useEffect(() => {
    if (NODE_ENV !== 'production') return;

    if (hasGA && gaId) trackGAPageview(gaId);

    if (hasPixel || hasOaiPixel)
      trackMetaConversion({ event: 'PageView', skipCapi: true });
  }, [pathname, hasGA, hasPixel, hasOaiPixel, gaId]);

  if (NODE_ENV !== 'production') return null;
  if (!hasGA && !hasPixel && !hasGtm && !hasOaiPixel) return null;

  return (
    <>
      {/* No noscript frames anywhere here: this component renders only after
          hydration, so a noscript inside it can never be shown to a no-JS
          visitor. The Meta pixel stays native and out of GTM — the CAPI
          mirror depends on browser and server sharing an event_id. */}
      {hasGtm && (
        <Script id="gtm-init" strategy="afterInteractive">{`
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${gtmId}');
        `}</Script>
      )}

      {hasGA && (
        <>
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${gaId}`}
            strategy="afterInteractive"
          />
          <Script id="ga4-init" strategy="afterInteractive">{`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${gaId}');
          `}</Script>
        </>
      )}

      {/* ChatGPT Ads pixel (OpenAI oaiq) — init only; events fire from the
          shared conversion fan-out in lib/analytics. The SDK captures the
          oppref click id from the landing URL into its own cookie. */}
      {hasOaiPixel && (
        <Script id="oaiq-init" strategy="afterInteractive">{`
          !function(w,d,s,u){if(w.oaiq)return;var q=function(){q.q.push(arguments)};
          q.q=[];w.oaiq=q;var j=d.createElement(s);j.async=1;j.src=u;
          var f=d.getElementsByTagName(s)[0];f.parentNode.insertBefore(j,f)
          }(window,document,"script","https://bzrcdn.openai.com/sdk/oaiq.min.js");
          oaiq("init",{pixelId:"${oaiPixelId}"});
        `}</Script>
      )}

      {/* Meta Pixel — init only; PageView fires from React effect so it shares event_id with the CAPI mirror */}
      {hasPixel && (
        <Script id="meta-pixel-init" strategy="afterInteractive">{`
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window,document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${pixelId}');
        `}</Script>
      )}
    </>
  );
}
